import React, { useState, useEffect, useCallback } from 'react';
import { complianceAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { RefreshCw, Plus, Trash2, ClipboardCheck, Pencil, X } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const KINDS = [
  { value: 'disclosure_event', label: 'Disclosure event' },
  { value: 'acknowledgement', label: 'Acknowledgement' },
  { value: 'ip_review', label: 'IP review' },
  { value: 'engagement_conflict_check', label: 'Engagement conflict check' },
];

const DISCLOSURE_STATUSES = ['draft', 'submitted', 'acknowledged', 'active', 'superseded'];
const CONFLICT_DECISIONS = ['clear', 'blocked', 'escalate'];

const emptyForm = () => ({
  kind: 'disclosure_event',
  externalId: '',
  status: 'draft',
  evidenceRefs: '',
  // disclosure payload fields (COI register)
  secondary_hours_week: '15',
  activities_summary: '',
  ip_carveout_confirmed: false,
  no_st_customers_confirmed: false,
  no_st_tools_ip_confirmed: false,
  st_ack_status: 'pending',
  notes: '',
  // conflict check payload
  engagement_id: '',
  vertical: 'maritime',
  customer_name_or_code: '',
  overlaps_st_customer: false,
  uses_st_ip_or_tools: false,
  decision: 'escalate',
  reviewed_by: '',
});

const AdminCompliance = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await complianceAPI.getAll({
        limit: 100,
        kind: kindFilter || undefined,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load COI register');
    } finally {
      setLoading(false);
    }
  }, [kindFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (row) => {
    const p = row.payload || {};
    setEditingId(row._id);
    setForm({
      ...emptyForm(),
      kind: row.kind,
      externalId: row.externalId || '',
      status: row.status || 'draft',
      evidenceRefs: (row.evidenceRefs || []).join(', '),
      secondary_hours_week: String(p.secondary_hours_week ?? 15),
      activities_summary: p.activities_summary || '',
      ip_carveout_confirmed: Boolean(p.ip_carveout_confirmed),
      no_st_customers_confirmed: Boolean(p.no_st_customers_confirmed),
      no_st_tools_ip_confirmed: Boolean(p.no_st_tools_ip_confirmed),
      st_ack_status: p.st_ack_status || 'pending',
      notes: p.notes || '',
      engagement_id: p.engagement_id || '',
      vertical: p.vertical || 'maritime',
      customer_name_or_code: p.customer_name_or_code || '',
      overlaps_st_customer: Boolean(p.overlaps_st_customer),
      uses_st_ip_or_tools: Boolean(p.uses_st_ip_or_tools),
      decision: p.decision || 'escalate',
      reviewed_by: p.reviewed_by || '',
    });
    setShowForm(true);
  };

  const buildPayload = () => {
    if (form.kind === 'engagement_conflict_check') {
      return {
        engagement_id: form.engagement_id,
        vertical: form.vertical,
        customer_name_or_code: form.customer_name_or_code,
        overlaps_st_customer: form.overlaps_st_customer,
        uses_st_ip_or_tools: form.uses_st_ip_or_tools,
        decision: form.decision,
        reviewed_by: form.reviewed_by,
        notes: form.notes || null,
      };
    }
    // disclosure / ack / ip — store disclosure-shaped fields in payload
    return {
      secondary_hours_week: Number(form.secondary_hours_week) || 15,
      activities_summary: form.activities_summary,
      ip_carveout_confirmed: form.ip_carveout_confirmed,
      no_st_customers_confirmed: form.no_st_customers_confirmed,
      no_st_tools_ip_confirmed: form.no_st_tools_ip_confirmed,
      st_ack_status: form.st_ack_status,
      notes: form.notes || null,
    };
  };

  const handleSave = async () => {
    const body = {
      kind: form.kind,
      externalId: form.externalId || undefined,
      status: form.status,
      evidenceRefs: form.evidenceRefs
        ? form.evidenceRefs.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      payload: buildPayload(),
      decidedAt: form.kind === 'engagement_conflict_check' ? new Date().toISOString() : undefined,
    };
    try {
      if (editingId) {
        await complianceAPI.update(editingId, body);
        toast.success('COI record updated');
      } else {
        await complianceAPI.create(body);
        toast.success('COI record created');
      }
      setShowForm(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this COI register entry?')) return;
    try {
      await complianceAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const kindLabel = (k) => KINDS.find((x) => x.value === k)?.label || k;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ClipboardCheck size={28} color="#ef4444" /> COI / Compliance
            </h1>
            <p style={{ color: '#a1a1aa' }}>
              Malta Track C register — disclosure, ST ack, IP review, per-engagement conflict checks.
              {total ? ` ${total} records.` : ' Empty until Gate 1 / conflict checks are logged.'}
            </p>
          </div>
          <div className="admin-header-right">
            <button onClick={openCreate} style={btnPrimary('#ef4444')}>
              <Plus size={15} /> New record
            </button>
            <button className="btn-secondary" onClick={load}><RefreshCw size={16} /></button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <Chip active={!kindFilter} onClick={() => setKindFilter('')} label="All kinds" />
          {KINDS.map((k) => (
            <Chip key={k.value} active={kindFilter === k.value} onClick={() => setKindFilter(k.value)} label={k.label} />
          ))}
        </div>

        {showForm && (
          <FormCard title={editingId ? 'Edit COI record' : 'New COI record'} onClose={() => setShowForm(false)}>
            <div style={grid2}>
              <Field label="Kind *">
                <select value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))} style={input}>
                  {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </Field>
              <Field label="External ID">
                <input value={form.externalId} onChange={(e) => setForm((p) => ({ ...p, externalId: e.target.value }))}
                  placeholder="e.g. COI-DISC-2026-001" style={input} />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} style={input}>
                  {DISCLOSURE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="pending">pending</option>
                  <option value="clear">clear</option>
                  <option value="blocked">blocked</option>
                  <option value="escalate">escalate</option>
                </select>
              </Field>
              <Field label="Evidence refs (comma-separated)">
                <input value={form.evidenceRefs} onChange={(e) => setForm((p) => ({ ...p, evidenceRefs: e.target.value }))}
                  placeholder="vault/letter.pdf, email-msg-id" style={input} />
              </Field>
            </div>

            {form.kind === 'engagement_conflict_check' ? (
              <div style={{ ...grid2, marginTop: 12 }}>
                <Field label="Engagement ID"><input value={form.engagement_id} onChange={(e) => setForm((p) => ({ ...p, engagement_id: e.target.value }))} style={input} /></Field>
                <Field label="Customer name / code"><input value={form.customer_name_or_code} onChange={(e) => setForm((p) => ({ ...p, customer_name_or_code: e.target.value }))} style={input} /></Field>
                <Field label="Vertical">
                  <select value={form.vertical} onChange={(e) => setForm((p) => ({ ...p, vertical: e.target.value }))} style={input}>
                    {['maritime', 'superyacht', 'electronics', 'pharma', 'medtech', 'aerospace', 'other'].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Field>
                <Field label="Decision">
                  <select value={form.decision} onChange={(e) => setForm((p) => ({ ...p, decision: e.target.value }))} style={input}>
                    {CONFLICT_DECISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Reviewed by"><input value={form.reviewed_by} onChange={(e) => setForm((p) => ({ ...p, reviewed_by: e.target.value }))} style={input} /></Field>
                <label style={checkLabel}><input type="checkbox" checked={form.overlaps_st_customer} onChange={(e) => setForm((p) => ({ ...p, overlaps_st_customer: e.target.checked }))} /> Overlaps ST customer</label>
                <label style={checkLabel}><input type="checkbox" checked={form.uses_st_ip_or_tools} onChange={(e) => setForm((p) => ({ ...p, uses_st_ip_or_tools: e.target.checked }))} /> Uses ST IP / tools</label>
                <Field label="Notes" full><textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} style={input} /></Field>
              </div>
            ) : (
              <div style={{ ...grid2, marginTop: 12 }}>
                <Field label="Secondary hours / week"><input type="number" value={form.secondary_hours_week} onChange={(e) => setForm((p) => ({ ...p, secondary_hours_week: e.target.value }))} style={input} /></Field>
                <Field label="ST ack status"><input value={form.st_ack_status} onChange={(e) => setForm((p) => ({ ...p, st_ack_status: e.target.value }))} style={input} /></Field>
                <Field label="Activities summary" full>
                  <textarea value={form.activities_summary} onChange={(e) => setForm((p) => ({ ...p, activities_summary: e.target.value }))} rows={2} style={input} placeholder="Plain-language MeccaFlex activities" />
                </Field>
                <label style={checkLabel}><input type="checkbox" checked={form.ip_carveout_confirmed} onChange={(e) => setForm((p) => ({ ...p, ip_carveout_confirmed: e.target.checked }))} /> IP carve-out confirmed</label>
                <label style={checkLabel}><input type="checkbox" checked={form.no_st_customers_confirmed} onChange={(e) => setForm((p) => ({ ...p, no_st_customers_confirmed: e.target.checked }))} /> No ST customers confirmed</label>
                <label style={checkLabel}><input type="checkbox" checked={form.no_st_tools_ip_confirmed} onChange={(e) => setForm((p) => ({ ...p, no_st_tools_ip_confirmed: e.target.checked }))} /> No ST tools/IP confirmed</label>
                <Field label="Notes" full><textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} style={input} /></Field>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={handleSave} style={btnPrimary('#ef4444')}>Save</button>
              <button onClick={() => setShowForm(false)} style={btnGhost}>Cancel</button>
            </div>
          </FormCard>
        )}

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr style={theadRow}>
                {['Kind', 'External ID', 'Status', 'Payload summary', 'Created', ''].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={emptyCell}><RefreshCw size={18} /> Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} style={emptyCell}>
                  No COI records yet. Log Gate 1 disclosure and per-engagement conflict checks here (Special Agent owns final copy).
                </td></tr>
              ) : items.map((row) => (
                <tr key={row._id} style={tbodyRow}>
                  <td style={td}><span style={badge('#ef4444')}>{kindLabel(row.kind)}</span></td>
                  <td style={td}>{row.externalId || '—'}</td>
                  <td style={td}>{row.status || '—'}</td>
                  <td style={{ ...td, color: '#a1a1aa', fontSize: 12, maxWidth: 280 }}>
                    {row.kind === 'engagement_conflict_check'
                      ? `${row.payload?.customer_name_or_code || '—'} · ${row.payload?.decision || '—'}`
                      : (row.payload?.activities_summary || row.payload?.st_ack_status || '—')}
                  </td>
                  <td style={td}>{fmt(row.createdAt)}</td>
                  <td style={td}>
                    <button onClick={() => openEdit(row)} style={iconBtn} title="Edit"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(row._id)} style={iconBtn} title="Delete"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

function Chip({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
      border: active ? '2px solid #ef4444' : '1px solid #27272a',
      background: active ? '#ef444420' : '#18181b',
      color: active ? '#fca5a5' : '#a1a1aa',
      fontWeight: active ? 600 : 400,
    }}>{label}</button>
  );
}

function FormCard({ title, onClose, children }) {
  return (
    <div style={{ background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 24, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#fff' }}>{title}</h3>
        <button onClick={onClose} style={iconBtn}><X size={16} /></button>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label style={{ gridColumn: full ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
      <span style={{ color: '#71717a' }}>{label}</span>
      {children}
    </label>
  );
}

const input = { background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const checkLabel = { display: 'flex', alignItems: 'center', gap: 8, color: '#a1a1aa', fontSize: 13 };
const btnPrimary = (c) => ({ background: c, border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 });
const btnGhost = { background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' };
const iconBtn = { background: 'transparent', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: 6 };
const tableWrap = { background: '#111', borderRadius: 12, overflow: 'hidden', border: '1px solid #27272a' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const theadRow = { background: '#18181b', borderBottom: '1px solid #27272a' };
const th = { padding: '12px 16px', color: '#71717a', fontWeight: 600, textAlign: 'left' };
const td = { padding: '14px 16px', color: '#fff' };
const tbodyRow = { borderBottom: '1px solid #1c1c1c' };
const emptyCell = { padding: 40, textAlign: 'center', color: '#71717a' };
const badge = (c) => ({ background: `${c}20`, color: c, borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 });

export default AdminCompliance;
